import React, { useCallback, useEffect, useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  TextInput,
  SelectInput,
  BooleanField,
  FunctionField,
  ListProps,
  useNotify,
  useRefresh,
  useListContext,
  useUnselectAll,
  Button as RaButton,
  TopToolbar,
  FilterButton,
  ExportButton,
} from 'react-admin';
import { Button, Chip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import useAuthorize from '../hooks/useAuthorize';
import { LEADS_MODULE_ROLES } from '../roles/portalRoles';
import { queueLeadEnrichment, syncLeadCredcoLinkage, getLeadEnrichmentPhase } from './leadsApi';
import EnrichmentPhaseBanner, { EnrichmentPhaseInfo } from './EnrichmentPhaseBanner';

const bucketColors: Record<string, 'warning' | 'default' | 'success'> = {
  Gold: 'warning',
  Silver: 'default',
  Bronze: 'success',
};

const BucketField = ({ record }: { record?: any }) => {
  if (!record?.bucket) return <>-</>;
  return <Chip size="small" label={record.bucket} color={bucketColors[record.bucket] || 'default'} />;
};

const CredcoBadge = ({ record }: { record?: any }) => {
  if (!record?.is_credco_customer) return <>-</>;
  return <Chip size="small" color="success" label="On CredCo" />;
};

const filters = [
  <TextInput label="Search" source="q" alwaysOn key="q" resettable />,
  <SelectInput
    label="Bucket"
    source="bucket"
    key="bucket"
    resettable
    choices={[
      { id: 'Gold', name: 'Gold' },
      { id: 'Silver', name: 'Silver' },
      { id: 'Bronze', name: 'Bronze' },
    ]}
  />,
  <SelectInput
    label="Lead Status"
    source="lead_status"
    key="lead_status"
    resettable
    choices={[
      { id: 'new', name: 'New' },
      { id: 'researching', name: 'Researching' },
      { id: 'qualified', name: 'Qualified' },
      { id: 'contacted', name: 'Contacted' },
      { id: 'onboarded', name: 'Onboarded' },
      { id: 'disqualified', name: 'Disqualified' },
    ]}
  />,
  <SelectInput
    label="Enrichment"
    source="enrichment_status"
    key="enrichment_status"
    resettable
    choices={[
      { id: 'pending', name: 'Pending' },
      { id: 'partial', name: 'Partial' },
      { id: 'complete', name: 'Complete' },
      { id: 'failed', name: 'Failed' },
    ]}
  />,
  <TextInput label="State" source="states" key="states" resettable />,
];

const BulkQueueEnrichmentButton = () => {
  const { selectedIds } = useListContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll('leadcompanies');

  const handleClick = useCallback(async () => {
    try {
      const result = await queueLeadEnrichment(selectedIds.map(Number));
      const skipped = Number(result.skipped_by_phase || 0);
      notify(
        skipped > 0
          ? `Queued ${result.queued} jobs (${skipped} skipped — outside active enrichment phase)`
          : `Queued ${result.queued} enrichment jobs`,
        { type: skipped > 0 ? 'warning' : 'info' }
      );
      unselectAll();
      refresh();
    } catch (error: any) {
      notify(error?.body?.error || error?.message || 'Failed to queue enrichment', { type: 'error' });
    }
  }, [notify, refresh, selectedIds, unselectAll]);

  return <RaButton label="Queue Enrichment" onClick={handleClick} />;
};

const ListActions = () => {
  const notify = useNotify();
  const refresh = useRefresh();

  const handleSync = async () => {
    try {
      const result = await syncLeadCredcoLinkage();
      notify(
        `CredCo linkage synced (${result.matchedCustomers} customers, ${result.matchedDirectory} directory)`,
        { type: 'info' }
      );
      refresh();
    } catch (error: any) {
      notify(error?.body?.error || error?.message || 'Linkage sync failed', { type: 'error' });
    }
  };

  return (
    <TopToolbar>
      <FilterButton />
      <ExportButton />
      <Button size="small" startIcon={<RefreshIcon />} onClick={handleSync}>
        Sync CredCo Linkage
      </Button>
    </TopToolbar>
  );
};

export const LeadCompanyList: React.FC<ListProps> = (props) => {
  useAuthorize(LEADS_MODULE_ROLES);
  const [enrichmentPhase, setEnrichmentPhase] = useState<EnrichmentPhaseInfo | null>(null);

  useEffect(() => {
    getLeadEnrichmentPhase()
      .then(setEnrichmentPhase)
      .catch(() => setEnrichmentPhase(null));
  }, []);

  return (
    <>
      <EnrichmentPhaseBanner phase={enrichmentPhase} />
      <List
      {...props}
      filters={filters}
      sort={{ field: 'credential_score', order: 'DESC' }}
      perPage={25}
      title="Lead Companies"
      actions={<ListActions />}
    >
      <Datagrid rowClick="show" bulkActionButtons={<BulkQueueEnrichmentButton />}>
        <TextField source="master_company_id" label="ID" />
        <TextField source="company_name" label="Company" />
        <FunctionField label="Bucket" render={(record: any) => <BucketField record={record} />} />
        <TextField source="buyer_seller_category" label="Category" />
        <NumberField source="credential_score" label="Credential" />
        <NumberField source="online_depth_score" label="Online Depth" />
        <TextField source="lead_status" label="Lead Status" />
        <TextField source="enrichment_status" label="Enrichment" />
        <BooleanField source="indiamart_listed" label="IndiaMART" />
        <TextField source="primary_website" label="Website" />
        <FunctionField label="CredCo" render={(record: any) => <CredcoBadge record={record} />} />
        <TextField source="states" label="State(s)" />
      </Datagrid>
    </List>
    </>
  );
};

export default LeadCompanyList;
